import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BRAND = "Nafsika's Garden";
const SITE = "https://nafsikasgarden.com";
const DEFAULT_IMAGE = `${SITE}/hero-d.webp`;
const DEFAULT_DESCRIPTION =
  "Discover Nafsika's Garden vegan cheese and plant-based foods. Enjoy delicious, gluten-free, soy-free, nut-free, and dairy-free cheese and spreads.";

export function toPlainText(value, max = 160) {
  if (!value) return "";
  const text = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= max) return text;

  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  const trimmed = (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim();
  return `${trimmed}…`;
}

function absoluteUrl(value, fallback) {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  return `${SITE}${value.startsWith("/") ? value : `/${value}`}`;
}

function upsertMeta(attribute, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    if (hreflang) tag.setAttribute("hreflang", hreflang);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default function PageMeta({ title, description, image, type = "website" }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageTitle = !title
      ? BRAND
      : title.includes(BRAND)
        ? title
        : `${title} | ${BRAND}`;
    const pageDescription = description || DEFAULT_DESCRIPTION;
    const pageUrl = `${SITE}${pathname === "/" ? "/" : pathname}`;
    const pageImage = absoluteUrl(image, DEFAULT_IMAGE);

    document.title = pageTitle;

    upsertMeta("name", "description", pageDescription);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:image", pageImage);
    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:locale", "en_US");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", "@nafsikasgarden");
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("name", "twitter:image", pageImage);

    upsertLink("canonical", pageUrl);
    upsertLink("alternate", pageUrl, "en");
    upsertLink("alternate", pageUrl, "x-default");
  }, [title, description, image, type, pathname]);

  return null;
}
